/**
 * <tecton-timeline-progress> — scroll-driven vertical timeline.
 *
 * Port of Timeline.tsx (about page). A fill line grows from 0 to 100% as the
 * component's midpoint travels through the viewport centre, and each item
 * activates once its dot rises past 60% of the viewport. Progress is written
 * to --tl-progress on the host and activation to data-active on each item;
 * all drawing happens in CSS.
 *
 * Under prefers-reduced-motion, or inside the theme editor, everything is
 * active and the line is full from the start — the section never renders
 * dimmed for someone who will not scroll it.
 */

class TectonTimelineProgress extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-tl-item]')];

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || window.Shopify?.designMode) {
      this.style.setProperty('--tl-progress', '100%');
      this.items.forEach((item) => item.setAttribute('data-active', ''));
      return;
    }

    this.onScroll = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    };

    // Horizon scrolls .page-wrapper, not the window — html and body are
    // overflow: hidden, so a window scroll listener never fires. A capturing
    // listener on the document sees scrolls from any container.
    document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  update() {
    const rect = this.getBoundingClientRect();
    const viewportH = window.innerHeight;

    const start = rect.top - viewportH * 0.5;
    const end = rect.top + rect.height - viewportH * 0.5;
    const progress = Math.max(0, Math.min(1, -start / (end - start)));
    this.style.setProperty('--tl-progress', `${progress * 100}%`);

    for (const item of this.items) {
      const dot = item.querySelector('[data-tl-dot]') || item;
      if (dot.getBoundingClientRect().top < viewportH * 0.6) {
        item.setAttribute('data-active', '');
      } else {
        item.removeAttribute('data-active');
      }
    }
  }
}

if (!customElements.get('tecton-timeline-progress')) {
  customElements.define('tecton-timeline-progress', TectonTimelineProgress);
}
